import React, {useCallback, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {Button} from '../../../styles/button';
import {Text} from '../../../styles/text';
import {Layout} from '../../../styles/layout';
import {modalTextColor, primaryColor} from '../../../utils/variables';
import VariationPicker from '../VariationPicker';
import VirtualCatalogList from '../VirtualCatalogList';
import {
    getProductPrice,
    getStoredCatalogShowImages,
    setStoredCatalogShowImages,
} from '../modeStorage';
import {useProductFilter} from '../useProductFilter';
import * as S from '../styles';
import {
    getProductPickerConfirmAddLabel,
    ProductPickResult,
    ProductPickerCategory,
    ProductPickerContext,
    ProductPickerItem,
    ProductPickerVariation,
} from '../types';
import {resolveProductDetails, willFetchProductDetails} from '../resolveProductDetails';

interface Props {
    products: ProductPickerItem[];
    categories?: ProductPickerCategory[];
    brands?: string[];
    context?: ProductPickerContext;
    disabled?: boolean;
    loading?: boolean;
    compact?: boolean;
    singlePick?: boolean;
    entityType?: 'goods' | 'service';
    onPick: (result: ProductPickResult) => void;
    fetchProductDetails?: (productId: number) => Promise<ProductPickerItem | null>;
    forceProductDetails?: boolean;
}

const CatalogPanelMode = ({
    products,
    categories: categoriesProp,
    brands: brandsProp,
    context,
    disabled,
    loading,
    compact,
    singlePick,
    entityType = 'goods',
    onPick,
    fetchProductDetails,
    forceProductDetails,
}: Props) => {
    const itemLabel = entityType === 'service' ? 'услугу' : 'товар';
    const itemsLabel = entityType === 'service' ? 'услуги' : 'товары';
    const confirmAddLabel = getProductPickerConfirmAddLabel(context);
    const [open, setOpen] = useState(false);
    const [brand, setBrand] = useState('');
    const [categoryId, setCategoryId] = useState<number>(0);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [pendingProduct, setPendingProduct] = useState<ProductPickerItem | null>(null);
    const [pendingVariation, setPendingVariation] = useState<ProductPickerVariation | null>(null);
    const [resolvingProduct, setResolvingProduct] = useState<ProductPickerItem | null>(null);
    const [showImages, setShowImages] = useState(() => getStoredCatalogShowImages());
    const resolvingRef = useRef(false);
    const {query, setQuery, results} = useProductFilter(products);

    const handleShowImagesToggle = () => {
        setShowImages((prev) => {
            const next = !prev;
            setStoredCatalogShowImages(next);
            return next;
        });
    };

    const brands = useMemo(() => {
        if (brandsProp) return brandsProp;
        const set = new Set<string>();
        products.forEach((p) => {
            if (p.brand) set.add(p.brand);
        });
        return Array.from(set).sort((a, b) => a.localeCompare(b, 'ru'));
    }, [brandsProp, products]);

    const categories = useMemo(() => {
        if (categoriesProp?.length) return categoriesProp;
        const map = new Map<number, ProductPickerCategory>();
        products.forEach((p) => {
            const id = Number(p.category_id) || 0;
            if (!id) return;
            const current = map.get(id);
            if (current) {
                current.count = (current.count || 0) + 1;
            } else {
                map.set(id, {id, name: p.category || `Категория #${id}`, count: 1});
            }
        });
        return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name, 'ru'));
    }, [categoriesProp, products]);

    const filtered = useMemo(() => {
        return results.filter((product) => {
            if (brand && product.brand !== brand) return false;
            if (categoryId && Number(product.category_id) !== categoryId) return false;
            return true;
        });
    }, [results, brand, categoryId]);

    const toggleSelected = useCallback((id: number) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
        );
    }, []);

    const commitOne = useCallback((product: ProductPickerItem, variation?: ProductPickerVariation | null) => {
        onPick({
            product,
            variation: variation || null,
            quantity: 1,
            price: variation?.price ?? getProductPrice(product),
        });
        if (singlePick) {
            setOpen(false);
            setPendingProduct(null);
            setPendingVariation(null);
            setSelectedIds([]);
        }
    }, [onPick, singlePick]);

    const startPick = useCallback(async (product: ProductPickerItem) => {
        if (resolvingRef.current) return;
        resolvingRef.current = true;

        const fetchOptions = {force: forceProductDetails};
        const showLoading = willFetchProductDetails(product, fetchProductDetails, fetchOptions);
        if (showLoading) {
            setResolvingProduct(product);
        }

        try {
            const resolved = await resolveProductDetails(product, fetchProductDetails, fetchOptions);
            if (resolved.has_variation) {
                setPendingProduct(resolved);
                setPendingVariation(null);
                return;
            }
            commitOne(resolved, null);
            if (!singlePick) {
                setSelectedIds((prev) => prev.filter((id) => id !== resolved.id));
            }
        } finally {
            resolvingRef.current = false;
            setResolvingProduct(null);
        }
    }, [forceProductDetails, fetchProductDetails, singlePick, commitOne]);

    const addSelected = async () => {
        if (resolvingRef.current) return;
        resolvingRef.current = true;

        const selected = products.filter((p) => selectedIds.includes(p.id));
        const needsLoad = selected.some((product) =>
            willFetchProductDetails(product, fetchProductDetails, {force: forceProductDetails}),
        );
        if (needsLoad && selected[0]) {
            setResolvingProduct(selected[0]);
        }

        try {
            const resolvedSelected = await Promise.all(
                selected.map((product) => resolveProductDetails(product, fetchProductDetails, {
                    force: forceProductDetails,
                })),
            );

            const withVariations = resolvedSelected.filter((p) => p.has_variation);
            const plain = resolvedSelected.filter((p) => !p.has_variation);

            plain.forEach((product) => commitOne(product, null));

            if (withVariations.length > 0) {
                setPendingProduct(withVariations[0]);
                setPendingVariation(null);
                setSelectedIds(withVariations.slice(1).map((p) => p.id));
                return;
            }

            setSelectedIds([]);
            setOpen(false);
        } finally {
            resolvingRef.current = false;
            setResolvingProduct(null);
        }
    };

    const panel = open
        ? createPortal(
            <S.PanelOverlay onClick={() => !pendingProduct && setOpen(false)}>
                <S.Panel onClick={(e) => e.stopPropagation()}>
                    <S.PanelHeader>
                        <Layout vertical between={2}>
                            <Text extent="M" weight="bold" color={primaryColor}>
                                {singlePick ? `Выбрать ${itemLabel}` : `Добавить ${itemsLabel}`}
                            </Text>
                            <Text extent="S" color={modalTextColor}>
                                {singlePick
                                    ? 'Нажмите на строку, чтобы выбрать'
                                    : 'Поиск, фильтры и мультивыбор'}
                            </Text>
                        </Layout>
                        <Button heightSize="32px" onClick={() => setOpen(false)}>
                            Закрыть
                        </Button>
                    </S.PanelHeader>

                    <S.PanelBody>
                        {resolvingProduct ? (
                            <S.DetailsLoadingCard style={{boxShadow: 'none', minWidth: '100%'}}>
                                <S.DetailsLoadingSpinner />
                                <Text extent="S" weight="bold" color={primaryColor}>
                                    {resolvingProduct.has_variation
                                        ? 'Загрузка вариаций…'
                                        : 'Загрузка товара…'}
                                </Text>
                                <Text extent="S" color={modalTextColor}>
                                    {resolvingProduct.name}
                                </Text>
                            </S.DetailsLoadingCard>
                        ) : pendingProduct ? (
                            <VariationPicker
                                product={pendingProduct}
                                selectedId={pendingVariation?.id}
                                onSelect={setPendingVariation}
                                onCancel={() => {
                                    setPendingProduct(null);
                                    setPendingVariation(null);
                                }}
                                onConfirm={() => {
                                    if (!pendingVariation || !pendingProduct) return;
                                    commitOne(pendingProduct, pendingVariation);
                                    if (singlePick) return;
                                    const nextId = selectedIds[0];
                                    const next = products.find((p) => p.id === nextId) || null;
                                    setSelectedIds((prev) => prev.slice(1));
                                    setPendingProduct(next);
                                    setPendingVariation(null);
                                    if (!next) {
                                        setOpen(false);
                                    }
                                }}
                                confirmDisabled={!pendingVariation}
                            />
                        ) : (
                            <>
                                <S.SearchShell>
                                    <S.SearchInput
                                        value={query}
                                        disabled={disabled || loading}
                                        placeholder="Поиск по названию, артикулу, бренду"
                                        onChange={(e) => setQuery(e.target.value)}
                                        autoFocus
                                    />
                                </S.SearchShell>

                                <S.CatalogListToolbar>
                                    <Layout between={8} vAlign="center" style={{flex: 1, minWidth: 0}}>
                                        <S.FilterSelect
                                            value={categoryId || ''}
                                            onChange={(e) => setCategoryId(Number(e.target.value) || 0)}
                                        >
                                            <option value="">Все категории</option>
                                            {categories.map((category) => (
                                                <option key={category.id} value={category.id}>
                                                    {category.name}
                                                    {category.count != null ? ` (${category.count})` : ''}
                                                </option>
                                            ))}
                                        </S.FilterSelect>
                                        <S.FilterSelect
                                            value={brand}
                                            onChange={(e) => setBrand(e.target.value)}
                                        >
                                            <option value="">Все бренды</option>
                                            {brands.map((item) => (
                                                <option key={item} value={item}>{item}</option>
                                            ))}
                                        </S.FilterSelect>
                                    </Layout>
                                    <S.CatalogShowImagesToggle
                                        type="button"
                                        $active={showImages}
                                        onClick={handleShowImagesToggle}
                                        title={showImages ? 'Скрыть фото в списке' : 'Показать фото в списке'}
                                    >
                                        {showImages ? 'С фото' : 'Без фото'}
                                    </S.CatalogShowImagesToggle>
                                </S.CatalogListToolbar>

                                {loading && products.length === 0 ? (
                                    <S.SkeletonStack aria-hidden>
                                        <S.SkeletonRow />
                                        <S.SkeletonRow />
                                        <S.SkeletonRow />
                                        <S.SkeletonRow />
                                        <S.SkeletonRow />
                                    </S.SkeletonStack>
                                ) : (
                                    <VirtualCatalogList
                                        products={filtered}
                                        selectedIds={selectedIds}
                                        showImages={showImages}
                                        singlePick={singlePick}
                                        loading={loading}
                                        onToggle={toggleSelected}
                                        onPick={startPick}
                                    />
                                )}
                            </>
                        )}
                    </S.PanelBody>

                    {!pendingProduct && !singlePick && (
                        <S.PanelFooter>
                            <Text extent="S" color={modalTextColor}>
                                Выбрано: {selectedIds.length}
                            </Text>
                            <Layout between={8}>
                                <Button heightSize="35px" onClick={() => setSelectedIds([])}>
                                    Сбросить
                                </Button>
                                <Button
                                    heightSize="35px"
                                    disabled={selectedIds.length === 0}
                                    onClick={addSelected}
                                >
                                    {confirmAddLabel}
                                </Button>
                            </Layout>
                        </S.PanelFooter>
                    )}
                </S.Panel>
            </S.PanelOverlay>,
            document.body,
        )
        : null;

    return (
        <S.Root $compact={compact}>
            <S.TriggerButton
                type="button"
                $compact={compact}
                disabled={disabled || loading}
                onClick={() => setOpen(true)}
            >
                {singlePick ? `Добавить ${itemLabel}` : 'Добавить товар'}
            </S.TriggerButton>
            <S.Hint $compact={compact}>
                {singlePick
                    ? 'Каталог: выбор одной позиции'
                    : 'Панель каталога с поиском и мультивыбором'}
            </S.Hint>
            {panel}
        </S.Root>
    );
};

export default CatalogPanelMode;
